/** The editor screen: layout, top bar, keyboard shortcuts, drop-to-upload, autosave and polling. */

import type { Project } from "@studio/engine";
import { ArrowLeft, Clapperboard, FileSpreadsheet, Grid3x3, History, Map as MapIcon, MessageSquare, ScanEye, Pause, Play, Redo2, RectangleHorizontal, SkipBack, Undo2, Users } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api";
import { addMarker, copySelection, deleteSelection, duplicateSelection, paste, selectScene } from "./actions";
import { pump as pumpAutoPlace } from "./autoplace";
import { AnalysisDialog } from "./AnalysisDialog";
import { HandDialog } from "./HandDialog";
import { CompareDialog } from "./CompareDialog";
import { HistoryDialog, restoreProject } from "./HistoryDialog";
import { SpreadDialog } from "./SpreadDialog";
import { ImportDialog } from "./ImportDialog";
import { Inspector } from "./Inspector";
import { Library, uploadFiles } from "./Library";
import { Preview } from "./preview";
import { PeopleDialog } from "./PeopleDialog";
import { RenderPanel } from "./RenderPanel";
import { dropDraft, readDraft, startAutosave, type Draft } from "./save";
import { Stage } from "./Stage";
import { StrokeOrderDialog } from "./StrokeOrderDialog";
import { ShareDialog } from "./ShareDialog";
import { SharedLibraryDialog } from "./SharedLibraryDialog";
import { SubtitlesDialog } from "./SubtitlesDialog";
import { VoiceDialog } from "./VoiceDialog";
import { Splitters } from "./Splitters";
import { useEditor } from "./store";
import { Timeline } from "./Timeline";

export function Editor({ project, onExit }: { project: Project; onExit: () => void }) {
  const preview = useMemo(() => new Preview(project.id), [project.id]);
  const loaded = useEditor((s) => s.project?.id === project.id);
  const panel = useEditor((s) => s.panel);
  const [dropping, setDropping] = useState(false);
  const layout = useRef<HTMLDivElement>(null);

  const [draft] = useState<Draft | null>(() => {
    const d = readDraft(project.id);
    // only offer drafts with edits the server never got
    return d && JSON.stringify(d.project) !== JSON.stringify(project) ? d : null;
  });
  useEffect(() => {
    useEditor.getState().open(project);
    const stopSave = startAutosave();
    return () => {
      stopSave();
      preview.pause();
    };
  }, [project, preview]);

  // keep the preview in sync with edits, the playhead and analysis results
  useEffect(() => {
    const st = useEditor.getState();
    if (st.project) {
      preview.setProject(st.project);
      preview.sync(st.project, st.analysis);
    }
    return useEditor.subscribe((s, prev) => {
      if (!s.project) return;
      if (s.project !== prev.project) preview.setProject(s.project);
      if (s.project !== prev.project || s.analysis !== prev.analysis) preview.sync(s.project, s.analysis);
      if (s.analysis !== prev.analysis) void pumpAutoPlace();
      if ((s.time !== prev.time && !s.playing) || s.boardView !== prev.boardView) preview.requestDraw();
    });
  }, [preview]);

  usePolling(project.id);

  // programmatic seek (used by automated UI tests): window.dispatchEvent(new CustomEvent("studio:seek", { detail: 12.5 }))
  useEffect(() => {
    const seek = (e: Event) => {
      preview.pause();
      useEditor.getState().setTime(Number((e as CustomEvent).detail) || 0);
    };
    window.addEventListener("studio:seek", seek);
    return () => window.removeEventListener("studio:seek", seek);
  }, [preview]);
  useShortcuts(preview);

  // drop files anywhere to upload them
  useEffect(() => {
    const over = (e: DragEvent) => {
      if (e.dataTransfer?.types.includes("Files")) {
        e.preventDefault();
        setDropping(true);
      }
    };
    const leave = (e: DragEvent) => {
      if (!e.relatedTarget) setDropping(false);
    };
    const drop = (e: DragEvent) => {
      if (!e.dataTransfer?.files.length) return;
      e.preventDefault();
      setDropping(false);
      void uploadFiles([...e.dataTransfer.files]).then((errs) => errs.length && alert(errs.join("\n")));
    };
    window.addEventListener("dragover", over);
    window.addEventListener("dragleave", leave);
    window.addEventListener("drop", drop);
    return () => {
      window.removeEventListener("dragover", over);
      window.removeEventListener("dragleave", leave);
      window.removeEventListener("drop", drop);
    };
  }, []);

  if (!loaded) return null;
  return (
    <div className="editor" ref={layout}>
      <TopBar preview={preview} onExit={onExit} />
      <aside className="left">
        <Library />
      </aside>
      <main className="center">
        <Stage preview={preview} />
      </main>
      <aside className="right">
        <Inspector preview={preview} />
      </aside>
      <section className="bottom">
        <Timeline preview={preview} />
      </section>
      <Splitters editor={layout} />
      {panel === "render" && <RenderPanel />}
      {panel === "import" && <ImportDialog />}
      {panel === "history" && <HistoryDialog />}
      {panel === "compare" && <CompareDialog preview={preview} />}
      {panel === "subtitles" && <SubtitlesDialog />}
      {(panel === "share" || panel === "renders") && <ShareDialog tab={panel === "renders" ? "renders" : "links"} />}
      {panel === "people" && <PeopleDialog />}
      {panel === "library" && <SharedLibraryDialog />}
      {panel === "spread" && <SpreadDialog />}
      {panel && typeof panel === "object" && "strokes" in panel && <StrokeOrderDialog elementId={panel.strokes} />}
      <DraftBanner pid={project.id} found={draft} />
      {panel && typeof panel === "object" && "analysis" in panel && <AnalysisDialog assetId={panel.analysis} />}
      {panel && typeof panel === "object" && "hand" in panel && <HandDialog assetId={panel.hand} />}
      {panel && typeof panel === "object" && "voice" in panel && <VoiceDialog key={panel.voice} assetId={panel.voice} />}
      {dropping && <div className="drop-hint">Drop images, audio or fonts to upload</div>}
    </div>
  );
}

function TopBar({ preview, onExit }: { preview: Preview; onExit: () => void }) {
  const name = useEditor((s) => s.project!.name);
  const saveState = useEditor((s) => s.saveState);
  const saveError = useEditor((s) => s.saveError);
  const canUndo = useEditor((s) => s.past.length > 0);
  const canRedo = useEditor((s) => s.future.length > 0);
  const playing = useEditor((s) => s.playing);
  const overlays = useEditor((s) => s.overlays);
  const boardView = useEditor((s) => s.boardView);
  const openComments = useEditor((s) => s.comments.filter((c) => !c.resolved).length);
  const activeRenders = useEditor((s) => s.jobs.filter((j) => j.status === "running" || j.status === "queued").length);
  const st = useEditor.getState();
  const label = { saved: "Saved", dirty: "Unsaved", saving: "Saving…", error: "Save failed, retrying", conflict: "Changed elsewhere" }[saveState];
  return (
    <header className="topbar">
      <button className="icon" title="All projects" onClick={onExit}><ArrowLeft size={18} /></button>
      <strong className="proj-name">{name}</strong>
      <span className={`save ${saveState}`} title={saveError}>{label}</span>
      {saveState === "conflict" && <button className="link" onClick={() => location.reload()}>Reload</button>}
      <div className="sep" />
      <button className="icon" title="Version history" onClick={() => st.set({ panel: "history" })}><History size={17} /></button>
      <button className="icon" title="Undo (Ctrl+Z)" disabled={!canUndo} onClick={st.undo}><Undo2 size={17} /></button>
      <button className="icon" title="Redo (Ctrl+Shift+Z)" disabled={!canRedo} onClick={st.redo}><Redo2 size={17} /></button>
      <div className="sep" />
      <button className="icon" title="To start (Home)" onClick={() => { preview.pause(); st.setTime(0); }}><SkipBack size={17} /></button>
      <button className="icon play" title="Play / pause (Space)" onClick={() => preview.toggle()}>{playing ? <Pause size={18} /> : <Play size={18} />}</button>
      <div className="sep" />
      <button className={`icon ${overlays.safe ? "on" : ""}`} title="Safe areas" onClick={() => st.set({ overlays: { ...overlays, safe: !overlays.safe } })}><RectangleHorizontal size={17} /></button>
      <button className={`icon ${overlays.thirds ? "on" : ""}`} title="Rule of thirds" onClick={() => st.set({ overlays: { ...overlays, thirds: !overlays.thirds } })}><Grid3x3 size={17} /></button>
      <button className="icon" title="Check this frame against a server render" onClick={() => st.set({ panel: "compare" })}><ScanEye size={17} /></button>
      <button className={`icon ${boardView ? "on" : ""}`} title="Board view: the whole board, scene frames and camera (B)" onClick={() => st.set({ boardView: !boardView })}><MapIcon size={17} /></button>
      <div className="grow" />
      <button onClick={() => st.set({ panel: "people" })} title="Who can open and edit this project">
        <Users size={16} /> People
      </button>
      <button onClick={() => st.set({ panel: "share" })} title="Review links, renders and reviewers' comments">
        <MessageSquare size={16} /> Review{openComments ? ` (${openComments})` : ""}
      </button>
      <button onClick={() => st.set({ panel: "import" })} title="Create scenes and timing from a shot list (Markdown table, CSV or JSON)">
        <FileSpreadsheet size={16} /> Import shot list
      </button>
      <button className="primary" onClick={() => st.set({ panel: st.panel === "render" ? null : "render" })}>
        <Clapperboard size={16} /> Render{activeRenders ? ` (${activeRenders})` : ""}
      </button>
    </header>
  );
}

/** Poll analysis and jobs: every second while something runs, otherwise every 8 s. */
function usePolling(pid: string) {
  useEffect(() => {
    let timer = 0;
    let stopped = false;
    const tick = async () => {
      window.clearTimeout(timer);
      let busy = false;
      try {
        const [analysis, jobs, comments] = await Promise.all([api.analysis(pid), api.jobs(pid, "render,still"), api.comments(pid)]);
        if (stopped) return;
        if (JSON.stringify(useEditor.getState().comments) !== JSON.stringify(comments)) useEditor.setState({ comments });
        const prev = useEditor.getState().analysis;
        if (JSON.stringify(prev) !== JSON.stringify(analysis)) useEditor.setState({ analysis });
        useEditor.setState({ jobs });
        busy =
          Object.values(analysis).some((a) => a.job && ["queued", "running"].includes(a.job.status)) ||
          jobs.some((j) => ["queued", "running", "cancelling"].includes(j.status));
      } catch {
        /* offline or logged out: keep trying slowly */
      }
      if (!stopped) timer = window.setTimeout(tick, busy ? 1000 : 8000);
    };
    const now = () => void tick();
    window.addEventListener("studio:poll", now);
    void tick();
    return () => {
      stopped = true;
      window.clearTimeout(timer);
      window.removeEventListener("studio:poll", now);
    };
  }, [pid]);
}

function useShortcuts(preview: Preview) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      // only fields you type into swallow shortcuts; checkboxes, sliders and buttons don't
      const typing = t.closest("textarea, select, [contenteditable]") ||
        (t instanceof HTMLInputElement && !["checkbox", "radio", "range", "color", "button", "file"].includes(t.type));
      if (typing) return;
      if (t instanceof HTMLElement && t !== document.body && e.key === " ") t.blur();
      const st = useEditor.getState();
      const mod = e.ctrlKey || e.metaKey;
      const fps = st.project?.output.fps ?? 30;
      if (e.key === " ") {
        e.preventDefault();
        preview.toggle();
      } else if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        e.preventDefault();
        if (st.playing) preview.pause();
        const step = e.shiftKey ? 1 : 1 / fps;
        st.setTime(preview.frameTime(st.time + (e.key === "ArrowLeft" ? -step : step)));
      } else if (e.key === "Home") {
        st.setTime(0);
      } else if (e.key === "End") {
        st.setTime(preview.duration());
      } else if (mod && e.key.toLowerCase() === "z") {
        e.preventDefault();
        if (e.shiftKey) st.redo();
        else st.undo();
      } else if (mod && e.key.toLowerCase() === "y") {
        e.preventDefault();
        st.redo();
      } else if (mod && e.key.toLowerCase() === "a") {
        e.preventDefault();
        selectScene();
      } else if (mod && (e.key.toLowerCase() === "c" || e.key.toLowerCase() === "x")) {
        if (window.getSelection()?.toString()) return; // copying page text
        e.preventDefault();
        copySelection(e.key.toLowerCase() === "x");
      } else if (mod && e.key.toLowerCase() === "v") {
        e.preventDefault();
        const msg = paste(e.shiftKey);
        if (msg) alert(msg);
      } else if (mod && e.key.toLowerCase() === "d") {
        e.preventDefault();
        duplicateSelection();
      } else if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        deleteSelection();
      } else if (!mod && (e.key === "=" || e.key === "+" || e.key === "-" || e.key === "_" || e.key === "\\")) {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent("studio:zoom", { detail: e.key === "\\" ? "fit" : e.key === "-" || e.key === "_" ? "out" : "in" }));
      } else if (e.key.toLowerCase() === "m" && !mod) {
        addMarker();
      } else if (e.key.toLowerCase() === "b" && !mod) {
        st.set({ boardView: !st.boardView });
      } else if (e.key === "Escape") {
        st.select(null);
        st.set({ panel: null });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [preview]);
}

/** Offers unsaved edits found in browser storage (from a closed tab or a lost connection). */
function DraftBanner({ pid, found }: { pid: string; found: Draft | null }) {
  const [draft, setDraft] = useState<Draft | null>(found);
  if (!draft) return null;
  return (
    <div className="draft-banner">
      Unsaved changes from {new Date(draft.savedAt).toLocaleString()} were found in this browser.
      <button className="primary" onClick={() => { restoreProject(draft.project as Project); setDraft(null); }}>Restore them</button>
      <button onClick={() => { dropDraft(pid); setDraft(null); }}>Discard</button>
    </div>
  );
}
